import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ArrowLeft, Plus, Search, Pencil, Trash2, MoreVertical,
  User, Building2, ShieldCheck, Users,
} from 'lucide-react';
import { useClientes, usePresupuestos } from '@/hooks/useData';
import type { Cliente } from '@/lib/types';
import { AppLayout } from '@/components/AppLayout';

const TIPO_LABELS: Record<Cliente['tipo'], { label: string; icon: React.ReactNode; cls: string }> = {
  particular:   { label: 'Particular',   icon: <User size={12} />,        cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  empresa:      { label: 'Empresa',      icon: <Building2 size={12} />,   cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  aseguradora:  { label: 'Aseguradora',  icon: <ShieldCheck size={12} />, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const CLIENTE_VACIO: Omit<Cliente, 'id' | 'fechaAlta'> = {
  nombre: '', nif: '', direccion: '', ciudad: '', cp: '',
  email: '', telefono: '', notas: '', tipo: 'particular',
};

export default function Clientes() {
  const navigate = useNavigate();
  const { clientes, crear, actualizar, eliminar } = useClientes();
  const { lista: presupuestos } = usePresupuestos();
  const [busqueda, setBusqueda] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Cliente | null>(null);
  const [toDelete, setToDelete] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<Cliente, 'id' | 'fechaAlta'>>(CLIENTE_VACIO);

  const filtrados = clientes.filter(c => {
    if (!busqueda) return true;
    const q = busqueda.toLowerCase();
    return c.nombre.toLowerCase().includes(q) || c.nif.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
  });

  const openNew = () => { setEditTarget(null); setForm(CLIENTE_VACIO); setModalOpen(true); };
  const openEdit = (c: Cliente) => { setEditTarget(c); setForm({ nombre: c.nombre, nif: c.nif, direccion: c.direccion, ciudad: c.ciudad, cp: c.cp, email: c.email, telefono: c.telefono, notas: c.notas, tipo: c.tipo }); setModalOpen(true); };

  const handleSave = () => {
    if (!form.nombre.trim()) return;
    if (editTarget) actualizar({ ...editTarget, ...form });
    else crear(form);
    setModalOpen(false);
  };

  const countPresupuestos = (id: string) => presupuestos.filter(p => p.clienteId === id).length;

  return (
    <AppLayout title="Clientes" active="clientes">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Header acciones */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Buscar por nombre, NIF o email…" value={busqueda} onChange={e => setBusqueda(e.target.value)} className="pl-9" />
          </div>
          <Button onClick={openNew} className="gap-1.5 shrink-0"><Plus size={16} /> Nuevo cliente</Button>
        </div>

        {/* Contadores */}
        <div className="flex gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5"><Users size={14} /> <strong>{clientes.length}</strong> clientes</span>
          {(['particular', 'empresa', 'aseguradora'] as Cliente['tipo'][]).map(t => (
            <span key={t} className="flex items-center gap-1">
              {TIPO_LABELS[t].icon}
              <strong>{clientes.filter(c => c.tipo === t).length}</strong> {TIPO_LABELS[t].label.toLowerCase()}s
            </span>
          ))}
        </div>

        {/* Lista */}
        {filtrados.length === 0 ? (
          <div className="flex flex-col items-center py-20 text-center">
            <div className="h-14 w-14 rounded-2xl bg-muted flex items-center justify-center mb-3"><Users size={24} className="text-muted-foreground" /></div>
            <p className="font-semibold mb-1">{busqueda ? 'Sin resultados' : 'Aún no hay clientes'}</p>
            <p className="text-sm text-muted-foreground mb-4">{busqueda ? 'Prueba con otro término' : 'Empieza añadiendo tu primer cliente'}</p>
            {!busqueda && <Button onClick={openNew} className="gap-1.5"><Plus size={16} /> Nuevo cliente</Button>}
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="hidden md:grid grid-cols-[1fr_130px_180px_130px_80px_44px] gap-3 px-4 py-2 border-b border-border bg-muted/40">
              {['Nombre / Empresa', 'NIF / CIF', 'Email', 'Teléfono', 'Presup.', ''].map(h => (
                <span key={h} className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{h}</span>
              ))}
            </div>
            <AnimatePresence>
              {filtrados.map((c, i) => (
                <motion.div
                  key={c.id} layout
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className={`grid grid-cols-1 md:grid-cols-[1fr_130px_180px_130px_80px_44px] gap-2 md:gap-3 items-center px-4 py-3 hover:bg-muted/30 transition-colors ${i < filtrados.length - 1 ? 'border-b border-border' : ''}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium shrink-0 ${TIPO_LABELS[c.tipo].cls}`}>
                      {TIPO_LABELS[c.tipo].icon} <span className="hidden sm:inline">{TIPO_LABELS[c.tipo].label}</span>
                    </div>
                    <span className="font-medium text-sm truncate">{c.nombre}</span>
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">{c.nif || '—'}</span>
                  <span className="text-xs text-muted-foreground truncate">{c.email || '—'}</span>
                  <span className="text-xs text-muted-foreground">{c.telefono || '—'}</span>
                  <span className="text-xs font-medium text-center">
                    {countPresupuestos(c.id) > 0
                      ? <Badge variant="secondary" className="font-mono">{countPresupuestos(c.id)}</Badge>
                      : <span className="text-muted-foreground">—</span>
                    }
                  </span>
                  <div className="flex justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical size={15} /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        <DropdownMenuItem onClick={() => openEdit(c)}><Pencil size={14} className="mr-2" /> Editar</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setToDelete(c.id)} className="text-destructive focus:text-destructive"><Trash2 size={14} className="mr-2" /> Eliminar</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Modal editar / crear */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Editar cliente' : 'Nuevo cliente'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <F label="Tipo de cliente">
              <Select value={form.tipo} onValueChange={v => setForm(f => ({ ...f, tipo: v as Cliente['tipo'] }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="particular">👤 Particular</SelectItem>
                  <SelectItem value="empresa">🏢 Empresa</SelectItem>
                  <SelectItem value="aseguradora">🛡️ Aseguradora</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Nombre / Razón social *">
              <Input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Nombre completo o razón social" />
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="NIF / CIF">
                <Input value={form.nif} onChange={e => setForm(f => ({ ...f, nif: e.target.value }))} className="font-mono" placeholder="12345678A" />
              </F>
              <F label="Teléfono">
                <Input value={form.telefono} onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))} placeholder="600 000 000" />
              </F>
            </div>
            <F label="Email">
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="contacto@ejemplo.com" />
            </F>
            <F label="Dirección">
              <Input value={form.direccion} onChange={e => setForm(f => ({ ...f, direccion: e.target.value }))} placeholder="Calle, nº, piso" />
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Código postal">
                <Input value={form.cp} onChange={e => setForm(f => ({ ...f, cp: e.target.value }))} className="font-mono" placeholder="28001" />
              </F>
              <F label="Ciudad">
                <Input value={form.ciudad} onChange={e => setForm(f => ({ ...f, ciudad: e.target.value }))} placeholder="Madrid" />
              </F>
            </div>
            <F label="Notas internas">
              <Textarea value={form.notas} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} rows={2} placeholder="Notas o comentarios internos…" />
            </F>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={!form.nombre.trim()}>{editTarget ? 'Guardar cambios' : 'Crear cliente'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm eliminar */}
      <AlertDialog open={!!toDelete} onOpenChange={() => setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar cliente?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer. Los presupuestos existentes no se verán afectados.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (toDelete) eliminar(toDelete); setToDelete(null); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}

function F({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}
